import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import get from 'lodash/get'

// Components
import { Format } from '../../../..'
import { Button, ButtonGroup } from '@homebot/shared-ui-swashbuckle'
import {
  Heading,
  ProConList,
  Strong,
  Pro,
  Info,
} from '../../../../shared/Gem/GemDetail'
import { Player } from '../../../../shared/Player'
import MonthlyPayment from '../../MonthlyPayment'
import { BuyersLink } from '../../BuyersLink'
import CTAHeading from '../../CTAHeading'
import FeeRange from '../../FeeRange'

// Actions
import { trackHomeActivityMikasa } from '../../../../../actions/home'
import { searchYlopoListings } from '../../../../../actions/listings'

// Selectors
import { selectInstantOfferEnabled } from '../../../../../store/selectors/powerUps'
import {
  selectNewHomeTotalEquity,
  selectBuyerNewHomeBudgetWithCap,
  selectEightyLTVNewHomeMonthlyPayment,
} from '../../../../../store/selectors/home/equity/newHome'
import {
  selectNewHomeVideoId,
  selectNewHomeVideoType,
} from '../../../../../store/selectors/videos'
import { selectNewHomeMortgageRate } from '../../../../../store/selectors/mortgageRate'
import { selectShowBuyersLink } from '../../../../../store/selectors/routes'

import {
  selectDisplayYlopoSpecificContent,
  selectHasListingsEnabled,
} from '../../../../../store/selectors/listings'

// Utils
import history from '../../../../../history'

// Constants
import { newHome } from '../../../../../constants/equity'
import { OVERLAY_HELPERS } from '../../../../../constants/navigation'
import { VerifySellingPriceClick } from '../../../../../constants/trackingEvents'
import { dispatchEvent } from '../../../../../helpers/tagManager'
import { PurchasingPowerListingsCarousel } from './PurchasingPowerListingsCarousel'
import { FROM } from '../../../../../mappings/navigation'

// Styles
import styles from './NewHome.module.scss'
import { loadYlopo } from '../../../../../helpers/ylopo'
import { VIEWED_DREAM_HOME_PANEL } from '../../../../../constants/client/clientEvents'
import { InstantOfferButton } from '../../../../shared/EstimatedValue/InstantOffer/InstantOfferButton'
import useViewport from '../../../../../hooks/useViewport'

const { NEW_HOME } = FROM

export const NewHomeDetail = () => {
  const { isMedium } = useViewport()
  const dispatch = useDispatch()
  const instantOfferEnabled = useSelector(selectInstantOfferEnabled)

  const budget = useSelector(selectBuyerNewHomeBudgetWithCap)
  const totalEquity = useSelector(selectNewHomeTotalEquity)
  const monthlyPayment = useSelector(selectEightyLTVNewHomeMonthlyPayment)
  const mortgageRate = useSelector(selectNewHomeMortgageRate)
  const showBuyersLink = useSelector(selectShowBuyersLink)

  const videoId = useSelector(selectNewHomeVideoId)
  const videoType = useSelector(selectNewHomeVideoType)

  const showYlopoListings = useSelector(selectDisplayYlopoSpecificContent)
  const showListings = useSelector(selectHasListingsEnabled)

  useEffect(() => {
    dispatch(
      trackHomeActivityMikasa({
        eventName: VIEWED_DREAM_HOME_PANEL,
        eventProperties: {
          activity_source: 'user',
        },
      })
    )
  }, [dispatch])

  useEffect(() => {
    if (showYlopoListings) {
      loadYlopo(() => {
        window['YLOPO_WIDGETS'].widgetOnloadCallback = (
          _widgetType,
          listings,
          searchArgs
        ) => {
          // use search tags in data-search param to control multiple result sets
          dispatch(
            searchYlopoListings(listings, get(searchArgs, 'search.tag', ''))
          )
        }
      })
    }
  }, [dispatch, showYlopoListings])

  return (
    <>
      <Heading>
        <FormattedMessage
          id='Homeowner.purchasePower.dreamHomeHeading'
          defaultMessage="Put your hard-earned equity ({totalEquity}) to work. While you don't have to, if you aim for a {percent} down payment, you could buy a new home worth up to around {budget}!"
          values={{
            percent: (
              <Format.Percentage value={newHome.downPaymentPercent * 100} />
            ),
            totalEquity: (
              <Strong>
                <Format.Currency abbreviate value={totalEquity} />
              </Strong>
            ),
            budget: (
              <Strong>
                <Format.Currency abbreviate value={budget} />
              </Strong>
            ),
          }}
        />
      </Heading>
      {videoType && videoId && (
        <Player
          className={styles.video}
          videoType={videoType}
          videoId={videoId}
        />
      )}
      <ProConList className={styles.proCon}>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.dreamHomeOne'
            defaultMessage='Your new monthly principal and interest payment would be around {payment} if you did a {loanTerm}-year mortgage at {rate}.'
            values={{
              rate: <Format.Rate value={mortgageRate} />,
              loanTerm: newHome.loanTerm,
              payment: <MonthlyPayment value={monthlyPayment} />,
            }}
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.dreamHomeTwo'
            defaultMessage='Get more square footage, a larger home office, or switch neighborhoods - you have options!'
          />
        </Pro>
        <Info>
          <FeeRange />
        </Info>
      </ProConList>
      {showListings && <PurchasingPowerListingsCarousel />}
      <CTAHeading className={styles.cta}>
        <FormattedMessage
          id='Homeowner.purchasePower.ctaHeading'
          defaultMessage="So, what's next?"
        />
      </CTAHeading>

      <ButtonGroup
        gap='.25rem'
        width='100%'
        display='flex'
        justifyContent='center'
        orientation={isMedium ? 'vertical' : 'horizontal'}
      >
        {showBuyersLink && (
          <BuyersLink affordability={budget as number} from={NEW_HOME} />
        )}

        {instantOfferEnabled ? (
          <InstantOfferButton colorScheme='btn-secondary' />
        ) : (
          <Button
            colorScheme='btn-secondary'
            onClick={() => {
              dispatchEvent(VerifySellingPriceClick, {
                eventLabel: `DREAM_HOME_PURCHASE_POWER`, // Leaving as old event label for backwards compatibility
              })
              history.push(OVERLAY_HELPERS.tuneYourValue.buildPath())
            }}
          >
            <FormattedMessage
              id='Homeowner.purchasePower.dreamHomeButton.cma'
              defaultMessage='Verify selling price'
            />
          </Button>
        )}
      </ButtonGroup>
    </>
  )
}
